// features/pos/components/ProductSearch.tsx
// ENHANCED - Better Dropdown, Keyboard Navigation, Search History, Barcode Indicator

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Spinner } from '@/components/ui/Spinner';
import { searchProducts, ProductResult } from '../services/posApi';
import {
    Search, X, Barcode, Package, Clock,
    TrendingUp, ArrowRight, Hash, Tag
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface ProductSearchProps {
    value: string;
    onChange: (value: string) => void;
    onSelect: (product: ProductResult) => void;
    onBarcodeSubmit?: (barcode: string) => void;
    disabled?: boolean;
    inputRef?: React.RefObject<HTMLInputElement | null>;
}

const RECENT_SEARCHES_KEY = 'pos-recent-searches';
const MAX_RECENT_SEARCHES = 5;
const MIN_SEARCH_LENGTH = 2;

export function ProductSearch({ value, onChange, onSelect, onBarcodeSubmit, disabled, inputRef }: ProductSearchProps) {
    const user = useAuthStore((s) => s.user);
    const [isFocused, setIsFocused] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const localInputRef = useRef<HTMLInputElement>(null);
    const effectiveInputRef = inputRef || localInputRef;

    // Load recent searches
    useEffect(() => {
        try {
            const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
            if (saved) {
                const recent = JSON.parse(saved);
                if (Array.isArray(recent)) {
                    setRecentSearches(recent.slice(0, MAX_RECENT_SEARCHES));
                }
            }
        } catch { }
    }, []);

    // Save search to recent
    const saveToRecent = (query: string) => {
        if (query.length < MIN_SEARCH_LENGTH) return;
        const updated = [query, ...recentSearches.filter(s => s !== query)]
            .slice(0, MAX_RECENT_SEARCHES);
        setRecentSearches(updated);
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    };

    // Search query
    const { data: results, isLoading } = useQuery({
        queryKey: ['pos-search', user?.tenantId, value],
        queryFn: () => searchProducts(user!.tenantId, value),
        enabled: !!user?.tenantId && value.length >= MIN_SEARCH_LENGTH,
        staleTime: 30000,
    });

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowResults(false);
                setSelectedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Reset selected index when results change
    useEffect(() => {
        setSelectedIndex(-1);
    }, [results, value]);

    const handleSelect = (product: ProductResult) => {
        saveToRecent(value);
        onSelect(product);
        onChange('');
        setShowResults(false);
        setSelectedIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = value.trim();
            const items = results || [];

            // 1. If an item was actively highlighted via arrow keys
            if (selectedIndex >= 0 && items[selectedIndex]) {
                handleSelect(items[selectedIndex]);
                return;
            }

            // 2. Barcode scanner submission (immediate cart addition)
            if (onBarcodeSubmit && trimmed) {
                saveToRecent(trimmed);
                onBarcodeSubmit(trimmed);
                onChange('');
                setShowResults(false);
                setSelectedIndex(-1);
                return;
            }

            // 3. Fallback: select first match if results are loaded
            if (value.length >= MIN_SEARCH_LENGTH && items.length > 0) {
                handleSelect(items[0]);
                return;
            }
            return;
        }

        if (!showResults) return;

        const items = results || [];
        const maxIndex = items.length - 1;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, maxIndex));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, -1));
                break;
            case 'Escape':
                setShowResults(false);
                setSelectedIndex(-1);
                effectiveInputRef.current?.blur();
                break;
        }
    };

    // Scroll selected item into view
    useEffect(() => {
        if (selectedIndex >= 0 && containerRef.current) {
            const items = containerRef.current.querySelectorAll('[data-search-item]');
            if (items[selectedIndex]) {
                items[selectedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    const formatNaira = (val: string) => {
        const num = parseFloat(val || '0');
        if (num >= 1000000) return `₦${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
        return `₦${num.toLocaleString()}`;
    };

    const isBarcode = value.length > 8 && /^\d+$/.test(value);

    return (
        <div className="relative flex-1" ref={containerRef}>
            <div className="relative">
                {/* Search Icon with Animation */}
                <motion.div
                    animate={isLoading ? { rotate: [0, 10, -10, 0] } : {}}
                    transition={{ duration: 0.5, repeat: isLoading ? Infinity : 0 }}
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                >
                    {isBarcode ? (
                        <Barcode size={18} className="text-emerald-500" />
                    ) : (
                        <Search size={18} className="text-slate-400" />
                    )}
                </motion.div>

                <input
                    ref={effectiveInputRef}
                    type="text"
                    placeholder={isBarcode ? "Barcode detected - press Enter..." : "Search products by name, SKU, or scan barcode..."}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setShowResults(true);
                        setSelectedIndex(-1);
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        if (value.length >= MIN_SEARCH_LENGTH || recentSearches.length > 0) {
                            setShowResults(true);
                        }
                    }}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    className={clsx(
                        'w-full pl-12 pr-10 py-3 border-2 rounded-xl text-sm transition-all',
                        'bg-slate-50 focus:bg-white',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isFocused
                            ? 'border-emerald-400 ring-4 ring-emerald-500/10 shadow-sm'
                            : 'border-slate-200',
                        isBarcode && 'border-emerald-300 bg-emerald-50/30'
                    )}
                />

                {/* Clear Button */}
                {value && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        onClick={() => {
                            onChange('');
                            setShowResults(false);
                            setSelectedIndex(-1);
                            effectiveInputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
                        type="button"
                    >
                        <X size={16} />
                    </motion.button>
                )}

                {/* Loading Indicator */}
                {isLoading && value.length >= MIN_SEARCH_LENGTH && (
                    <div className="absolute right-12 top-1/2 -translate-y-1/2">
                        <Spinner size="xs" />
                    </div>
                )}
            </div>

            {/* Barcode Hint */}
            {isBarcode && value.length >= MIN_SEARCH_LENGTH && (
                <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute top-full mt-1 left-0 right-0 flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 z-50"
                >
                    <Barcode size={12} />
                    <span>Barcode detected</span>
                    <kbd className="ml-auto px-1.5 py-0.5 bg-emerald-100 rounded text-[10px] font-mono">Enter</kbd>
                    <span className="text-emerald-500">to search</span>
                </motion.div>
            )}

            {/* Search Results Dropdown */}
            <AnimatePresence>
                {showResults && (value.length >= MIN_SEARCH_LENGTH || (!value && recentSearches.length > 0)) && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.15, type: 'spring', stiffness: 400, damping: 30 }}
                        className="absolute top-full mt-2 left-0 right-0 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 max-h-96 overflow-hidden"
                    >
                        {/* Recent Searches */}
                        {!value && recentSearches.length > 0 && (
                            <div className="p-3 border-b border-slate-100">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <Clock size={12} className="text-slate-400" />
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        Recent Searches
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {recentSearches.map((search, idx) => (
                                        <motion.button
                                            key={idx}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: idx * 0.03 }}
                                            onClick={() => {
                                                onChange(search);
                                                effectiveInputRef.current?.focus();
                                            }}
                                            className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs text-slate-600 font-medium transition-colors"
                                            type="button"
                                        >
                                            <Clock size={10} className="text-slate-400" />
                                            {search}
                                        </motion.button>
                                    ))}
                                    <button
                                        onClick={() => {
                                            setRecentSearches([]);
                                            localStorage.removeItem(RECENT_SEARCHES_KEY);
                                        }}
                                        className="px-2.5 py-1.5 text-xs text-slate-400 hover:text-rose-500 font-medium transition-colors"
                                        type="button"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex items-center justify-center py-12">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    className="w-8 h-8 rounded-full border-3 border-emerald-200 border-t-emerald-600"
                                />
                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoading && value.length >= MIN_SEARCH_LENGTH && (!results || results.length === 0) && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex flex-col items-center justify-center py-12 text-slate-400 px-4"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                                    <Search size={28} className="text-slate-300" />
                                </div>
                                <p className="text-sm font-semibold text-slate-500">No products found</p>
                                <p className="text-xs text-slate-400 mt-1 text-center">
                                    Try a different search term or scan a barcode
                                </p>
                                <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                                    <span className="flex items-center gap-1">
                                        <Hash size={10} /> SKU
                                    </span>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <Tag size={10} /> Name
                                    </span>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <Barcode size={10} /> Barcode
                                    </span>
                                </div>
                            </motion.div>
                        )}

                        {/* Results List */}
                        {!isLoading && results && results.length > 0 && (
                            <div className="max-h-72 overflow-y-auto">
                                {/* Results count */}
                                <div className="px-4 py-2 border-b border-slate-50 bg-slate-50/50">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        {results.length} product{results.length !== 1 ? 's' : ''} found
                                    </p>
                                </div>

                                {results.map((product, idx) => (
                                    <motion.button
                                        key={product.id}
                                        data-search-item
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.02 }}
                                        onClick={() => handleSelect(product)}
                                        className={clsx(
                                            'w-full flex items-center gap-3 px-4 py-3 transition-colors text-left border-b border-slate-50 last:border-0',
                                            selectedIndex === idx
                                                ? 'bg-emerald-50 border-emerald-100'
                                                : 'hover:bg-slate-50'
                                        )}
                                        type="button"
                                    >
                                        <div className={clsx(
                                            'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden',
                                            selectedIndex === idx ? 'bg-emerald-100 ring-2 ring-emerald-300' : 'bg-slate-100'
                                        )}>
                                            {product.imageUrls?.[0] ? (
                                                <img
                                                    src={product.imageUrls[0]}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <Package size={20} className="text-slate-400" />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-slate-900 truncate">
                                                    {product.name}
                                                </p>
                                                {product.sku && (
                                                    <span className="text-[9px] text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                                                        {product.sku}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {product.categoryName || product.brand || '—'}
                                            </p>
                                            {product.barcode && (
                                                <p className="text-[9px] text-slate-400 font-mono mt-0.5 flex items-center gap-1">
                                                    <Barcode size={9} />
                                                    {product.barcode}
                                                </p>
                                            )}
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-bold text-emerald-600">
                                                {formatNaira(product.retailPrice || '0')}
                                            </p>
                                            {product.clearancePrice && (
                                                <p className="text-[10px] text-amber-600 font-bold bg-amber-50 px-1.5 py-0.5 rounded-full mt-0.5">
                                                    Sale {formatNaira(product.clearancePrice)}
                                                </p>
                                            )}
                                            {product.wholesalePrice && (
                                                <p className="text-[9px] text-blue-500">
                                                    W/S {formatNaira(product.wholesalePrice)}
                                                </p>
                                            )}
                                        </div>

                                        <ArrowRight
                                            size={14}
                                            className={clsx(
                                                'transition-all',
                                                selectedIndex === idx
                                                    ? 'text-emerald-500 opacity-100'
                                                    : 'text-slate-300 opacity-0'
                                            )}
                                        />
                                    </motion.button>
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        {results && results.length > 0 && (
                            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-[10px] text-slate-400">
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px] font-mono">↑↓</kbd>
                                    Navigate
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px] font-mono">↵</kbd>
                                    Select
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[9px] font-mono">Esc</kbd>
                                    Close
                                </span>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}